export const DEFAULT_Y_AXIS_WIDTH = 45;

const PIXELS_PER_TICK = 50;
const CHART_VERTICAL_INSET = 65;
const MINIMUM_TICK_COUNT = 2;

const TICK_STEP_MULTIPLIERS = [
  { threshold: Math.sqrt(50), multiplier: 10 },
  { threshold: Math.sqrt(10), multiplier: 5 },
  { threshold: Math.sqrt(2), multiplier: 2 },
];

const MAX_NICE_ITERATIONS = 10;
const STEP_COUNT_PRECISION = 12;

const TICK_LABEL_CHARACTER_WIDTH = 8;
const TICK_LABEL_RESERVED_SPACE = 20;

export const getMaxPlottedValue = (series: { data: (number | null)[] }[], stacked: boolean): number => {
  const pointCount = Math.max(0, ...series.map((seriesItem) => seriesItem.data.length));

  const valuesPerPoint = Array.from({ length: pointCount }, (_, index) => {
    const pointValues = series.map((seriesItem) => seriesItem.data[index] ?? 0);

    return stacked ? pointValues.reduce((total, value) => total + value, 0) : Math.max(0, ...pointValues);
  });

  return Math.max(0, ...valuesPerPoint);
};

const getTickStep = (maxValue: number, tickCount: number): number => {
  const rawStep = maxValue / tickCount;
  const power = Math.floor(Math.log10(rawStep));
  const error = rawStep / 10 ** power;
  const { multiplier } = TICK_STEP_MULTIPLIERS.find(({ threshold }) => error >= threshold) ?? { multiplier: 1 };

  return multiplier * 10 ** power;
};

const countSteps = (value: number, step: number): number => Number((value / step).toPrecision(STEP_COUNT_PRECISION));

const getNiceMaxValue = (maxValue: number, tickCount: number, previousStep = 0, iteration = 0): number => {
  const step = getTickStep(maxValue, tickCount);

  if (step === previousStep || iteration === MAX_NICE_ITERATIONS) {
    return maxValue;
  }

  return getNiceMaxValue(Math.ceil(countSteps(maxValue, step)) * step, tickCount, step, iteration + 1);
};

export const getTickValues = (maxValue: number, tickCount: number): number[] => {
  if (!Number.isFinite(maxValue) || maxValue <= 0) {
    return [0];
  }

  const niceMaxValue = getNiceMaxValue(maxValue, tickCount);
  const step = getTickStep(niceMaxValue, tickCount);
  const decimalCount = Math.max(0, -Math.floor(Math.log10(step)));

  return Array.from({ length: Math.round(countSteps(niceMaxValue, step)) + 1 }, (_, index) =>
    Number((index * step).toFixed(decimalCount))
  );
};

type YAxisWidthParams = {
  maxValue: number;
  chartHeight: number;
  valueFormatter?: (value: number | null) => string;
};

export const getYAxisWidth = ({ maxValue, chartHeight, valueFormatter }: YAxisWidthParams): number => {
  if (!valueFormatter) {
    return DEFAULT_Y_AXIS_WIDTH;
  }

  const tickCount = Math.max(MINIMUM_TICK_COUNT, Math.floor((chartHeight - CHART_VERTICAL_INSET) / PIXELS_PER_TICK));

  const labelLengths = [tickCount - 1, tickCount, tickCount + 1]
    .filter((candidateTickCount) => candidateTickCount >= MINIMUM_TICK_COUNT)
    .flatMap((candidateTickCount) =>
      getTickValues(maxValue, candidateTickCount).map((tickValue) => valueFormatter(tickValue).length)
    );

  const longestLabelLength = Math.max(0, ...labelLengths);

  return Math.max(DEFAULT_Y_AXIS_WIDTH, longestLabelLength * TICK_LABEL_CHARACTER_WIDTH + TICK_LABEL_RESERVED_SPACE);
};
